import { COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  topics: {
    title?: string;
    questionAndAnswer?: {
      subtitle?: string;
      qa?: {
        question: string;
        answer: string;
      }[];
    }[];
  }[];
  vocabulary: {
    faqText: string;
    searchPlaceholderText: string;
    searchErrorMessageText: string;
    searchResultCount: string;
    menuButtonText: string;
    sidebarTitleText: string;
    showAllText: string;
    hideAllText: string;
  };
  enableTopNavigation: boolean;
  confluence: string;
}

const propsSchema: PropsSchemaList = [
  {
    key: 'topics',
    label: 'Topic',
    type: types.ARRAY,
    children: [
      {
        key: 'title',
        label: 'Topic Title',
        type: types.TEXT,
      },
      {
        key: 'questionAndAnswer',
        label: 'Q&A (Question and Answer) with Subtitle',
        type: types.ARRAY,
        children: [
          {
            key: 'subtitle',
            label: 'Subtitle (Optional)',
            type: types.TEXT,
            tooltip: 'Adds a subtitle to the sidebar and above the question',
          },
          {
            key: 'qa',
            label: 'Question and Answer',
            type: types.ARRAY,
            children: [
              {
                key: 'question',
                label: 'Question',
                type: types.TEXT,
              },
              {
                key: 'answer',
                label: 'Answer',
                type: types.TINYMCE,
                options: {
                  // Limit amount of options for tinymce editor
                  menubar: 'table',
                  toolbar: 'link superscript subscript forecolor lineheight',
                  height: 200,
                },
              }
            ]
          },
        ]
      }
    ]
  },
  {
    key: 'vocabulary',
    label: 'Vocabulary',
    type: types.OBJECT,
    children: [
      {
        key: 'faqText',
        label: 'Frequently Asked Questions Text',
        type: types.TEXT,
        tooltip: 'On mobile, appears above the search bar and in the top menu bar when scrolling down'
      },
      {
        key: 'sidebarTitleText',
        label: 'Sidebar title text',
        type: types.TEXT,
        tooltip: 'The text that is above the list of sidebar topics'
      },
      {
        key: 'menuButtonText',
        label: 'Mobile Only - Menu button text',
        type: types.TEXT,
      },
      {
        key: 'showAllText',
        label: 'Q&A - Show all text',
        type: types.TEXT,
      },
      {
        key: 'hideAllText',
        label: 'Q&A - Hide all text',
        type: types.TEXT,
      },
      {
        key: 'searchPlaceholderText',
        label: 'Search bar placeholder text',
        type: types.TEXT,
        tooltip: 'The placeholder text inside the search bar'
      },
      {
        key: 'searchErrorMessageText',
        label: 'Search no results found text',
        type: types.TEXT,
        tooltip: 'A message when there are no search results'
      },
      {
        key: 'searchResultCount',
        label: 'Search result found text',
        type: types.TEXT,
        tooltip: '{{count}} is dynamically replaced with the number of results'
      },
    ]
  },
  {
    key: 'enableTopNavigation',
    label: 'Show FAQ navigation when scrolling down',
    type: types.BOOLEAN,
  },
  {
    key: 'confluence',
    label: 'Confluence FAQ Widget Guide',
    type: types.TEXT,
    tooltip: 'The documentation for this widget can be found at https://confluence.faradayfuture.com/display/FW/FAQ+Widget',
  },
];

const defaultProps: Props = {
  topics: [
    {
      title: 'Product',
      questionAndAnswer: [
        {
          subtitle: '',
          qa: [
            {
              question: 'How does regenerative braking work?',
              answer: ''
            },
            {
              question: 'Which app do I use to control my FF 91?',
              answer: ''
            },
          ]
        }
      ]
    },
    {
      title: 'Sales',
      questionAndAnswer: [
        {
          subtitle: '',
          qa: [
            {
              question: 'What is the difference between the Alliance and the Futurist variants?',
              answer: ''
            },
            {
              question: 'How do I sign up to demo drive the FF 91 before purchasing?',
              answer: '<span>Signing up for a demo drive ahead of time is encouraged. You can sign up for a demo drive by clicking here , or you can visit our one of our FF Experience locations and schedule a drive in person (subject to availability).</span>'
            },
            {
              question: 'How much does the FF 91 cost?',
              answer: ''
            },
            {
              question: 'What are my options for purchasing or leasing an FF 91?',
              answer: '<span>FF Customers are encouraged to seek financing via a lending institution of their choosing or pay for their vehicle in cash. FF will be offering lease options at a later time. Click here to sign up for more information and news regarding our leasing program</span>'
            },
            {
              question: 'Is financing available? How does this work?',
              answer: '<span>Financing is currently not available through FF. Customers are encouraged to seek financing via a lending institution of their choosing.</span>'
            },
            {
              question: 'Is the FF 91 eligible for tax incentives?',
              answer: '<span>[Sales team] [link to https://www.fueleconomy.gov/feg/taxcenter.shtml]</span>'
            },
            {
              question: 'How do I reserve an FF 91?',
              answer: `<span>Please click <a href='https://www.ff.com/us/design/2022/FF91/model'>here</a> Preorder Now.</span>`
            },
            {
              question: 'What is the benefit of purchasing the Alliance versus the Futurist edition?',
              answer: ''
            },
          ]
        }
      ]
    },
    {
      title: 'Delivery',
      questionAndAnswer: [
        {
          subtitle: '',
          qa: [
            {
              question: 'How do I take delivery of my FF 91?',
              answer: '<span>Your FF Sales Specialist will assist you in scheduling home delivery or pick up at one of our FF Experience Centers.</span>'
            },
            {
              question: 'When will I receive my FF 91?',
              answer: '<span>First deliveries start at the end of April 2023.</span>'
            },
          ]
        }
      ]
    },
    {
      title: 'Service',
      questionAndAnswer: [
        {
          subtitle: '',
          qa: [
            {
              question: 'What Faraday Future service support is available?',
              answer: '<span>Contact Faraday Future Service Support at 1-877-FF-HELP-U (1-877-334-3578) or ServiceSupport@ff.com to arrange a service appointment.</span>'
            },
          ]
        },
        {
          subtitle: 'Warranty',
          qa: [
            {
              question: 'What is the warranty on the FF 91?',
              answer: ''
            },
          ]
        },
        {
          subtitle: 'Process',
          qa: [
            {
              question: 'How do I request service for the FF 91, if needed?',
              answer: ''
            },
          ]
        },
        {
          subtitle: 'Maintenance',
          qa: [
            {
              question: 'What maintenance is required for the FF 91?',
              answer: ''
            },
            {
              question: 'How do I find the User Manual?',
              answer: ''
            },
            {
              question: `How do I find the First Responder's Guide?`,
              answer: ''
            },
          ]
        },
      ]
    },
    {
      title: 'Charging',
      questionAndAnswer: [
        {
          subtitle: 'Capabilities',
          qa: [
            {
              question: 'What type of battery does the FF 91 have?',
              answer: ''
            },
            {
              question: 'How long does it take to charge the FF 91?',
              answer: ''
            },
            {
              question: 'Do I need to charge daily?',
              answer: `<span>The FF 91 offers an industry-leading range of 381 miles per charge, making it feasible to drive for days without plugging it. However, with charging as easy as plugging in when you pull into your garage, it's simple and convenient to wake up to a fully charged vehicle.</span>`
            },
          ]
        },
        {
          subtitle: 'Locations',
          qa: [
            {
              question: 'How can I find charging stations for my FF 91?',
              answer: ''
            },
          ]
        },
        {
          subtitle: 'Charging options',
          qa: [
            {
              question: 'What are my options for home charging? How does it work?',
              answer: ''
            },
            {
              question: 'How can I get a home charging station installed?',
              answer: ''
            },
            {
              question: 'When should I install my home charging station?',
              answer: `<span>Faraday Future recommends scheduling installation of your home charging solution before you bring home your FF 91, to ensure you are ready to charge up from day 1. Learn more about [ Qmerit link ]</span>`,
            },
            {
              question: 'How far can I go with the FF 91 on a road trip?',
              answer: `<span>The FF 91 offers an industry-leading range of 381 miles per charge, making it feasible to drive long distances on a single charge. If needed, locate public charging stations along the way using the Vehicle CID [link to public charging info]</span>`
            },
            {
              question: 'Does the vehicle come with a portable charger?',
              answer: '<span>Every new FF 91 purchased from Faraday Future is equipped with an included Lectron Portable Charger, flexible for use for both Level 1 and Level 2 charging. [link to Lectron info]</span>'
            },
            {
              question: 'What happens if I run out of charge on the road?',
              answer: '<span>Please contact Roadside Assistance 24/7 at 1-888-551-6399.</span>'
            },
            {
              question: 'What is the difference between AC and DC charging?',
              answer: `<span>AC (Alternating Current) charging is also known as Level 1 or Level 2 charging and describes charging power levels from 1kW to 19.2kW. Level 1 chargers (including the Lectron Portable Charger provided with the FF 91) can be plugged into standard household outlets and provide slower charging; Level 2 chargers (home charging, plus some public charging options) require professional installation such as through Faraday Future partner Qmerit.
              DC (Direct Current) Fast Charging is typically available at select public charging stations, offering power levels from approximately 24kW - 350kW. The FF 91 vehicle can accept as much as XXkW under ideal conditions. [confirm with battery team]</span>`
            },
            {
              question: 'What public charging benefit comes with the FF 91? How do I sign up?',
              answer: ''
            },
          ]
        }
      ]
    },
  ],
  vocabulary: {
    faqText: 'Frequently Asked Questions',
    searchPlaceholderText: 'Search',
    searchErrorMessageText: 'No search results found',
    searchResultCount: '{{count}} result found',
    menuButtonText: 'Menu',
    sidebarTitleText: 'All',
    showAllText: 'Show All',
    hideAllText: 'Hide All',
  },
  enableTopNavigation: true,
  confluence: 'https://confluence.faradayfuture.com/display/FW/FAQ+Widget'
}

export default {
  title: 'FAQ (Frequently Asked Questions)',
  id: 'FAQ',
  propsSchema,
  defaultProps,
}
